/**
 * injectTierGuards() — Server-side tier enforcement for DDUISpec
 *
 * Post-processes any LLM-generated Spec to overwrite `visible[]` conditions
 * on premium components using the server-verified user tier. This is the
 * hard enforcement layer — Layer 2 of the two-layer tier defense.
 *
 * Layer 1 (soft): LLM prompt instructions — do not generate PatternSpotlight
 *                  for free users. Fast, cheap, but not reliable.
 * Layer 2 (hard): injectTierGuards() — overwrites visible[] unconditionally.
 *                  LLM hallucination cannot bypass this gate.
 *
 * INVARIANT: This function MUST be called server-side before the spec is
 * returned to the client. LLM output must NEVER reach the client with
 * unguarded premium components.
 *
 * Premium component gates (Sally's catalog spec — Discourse #3543/4):
 *   - PatternSpotlight      -> Drama+ or DramaX  (tier !== 'free')
 *   - TransformationInsight -> DramaX only        (tier === 'dramax')
 *     COMPOUND GATE: also requires impactScore >= 6 from element props
 *
 * Architecture ref: Winston (Discourse #3543/2) Section 3
 * Task: #783.S2 — [NocoDB #785]
 * Agent: Diego <diego@liftping.net>
 */

import type { Spec, UIElement, VisibilityCondition } from '@json-render/core';
import type { DDRuntimeState } from './schema';

// ============================================================================
// Premium component gate configuration
// ============================================================================

type GateConfig = {
  tier: 'drama_plus' | 'dramax';
  visibility: (props: Record<string, unknown>) => VisibilityCondition;
};

const PREMIUM_COMPONENT_GATES: Record<string, GateConfig> = {
  /**
   * PatternSpotlight — Drama+ only
   * Gate: tier is NOT 'free' (drama_plus or dramax users can see this)
   */
  PatternSpotlight: {
    tier: 'drama_plus',
    visibility: (_props: Record<string, unknown>): VisibilityCondition => ({
      $state: '/tier',
      neq: 'free',
    }),
  },

  /**
   * TransformationInsight — DramaX only, compound gate
   * Gate: tier === 'dramax' AND impactScore >= 6
   *
   * impactScore from props is set by LLM from real analysis data —
   * safe to use in compound condition. The tier check is always
   * overwritten server-side regardless of what LLM output says.
   *
   * See: Transformation.impactScore in packages/shared/src/types/analysis.ts
   */
  TransformationInsight: {
    tier: 'dramax',
    visibility: (props: Record<string, unknown>): VisibilityCondition => {
      const impactScore =
        typeof props['impactScore'] === 'number' ? props['impactScore'] : 0;
      return {
        $and: [
          // Tier gate: must be DramaX
          { $state: '/tier', eq: 'dramax' },
          // Impact gate: only show high-impact transformations (>= 6)
          // This is a static boolean resolved from the analysis data, not
          // a state expression, because impactScore is per-component data
          impactScore >= 6 ? true : false,
        ],
      };
    },
  },
};

/** All premium component names requiring tier enforcement */
export const PREMIUM_COMPONENT_NAMES = Object.keys(PREMIUM_COMPONENT_GATES);

// ============================================================================
// injectTierGuards()
// ============================================================================

/**
 * Post-process an LLM-generated Spec to inject server-enforced tier visibility
 * guards on all premium components.
 *
 * This function is idempotent — running it twice produces the same result.
 * It mutates the spec in-place and also returns it for convenience.
 *
 * @param spec   - LLM-generated Spec (mutated in-place, also returned)
 * @param state  - Server-verified DDRuntimeState (tier from Supabase/Polar)
 * @returns      - The same spec with tier guards applied
 *
 * @example
 * ```typescript
 * const spec = await generateSpecFromLLM(analysisResult, userTier);
 * // ALWAYS call this before returning to client:
 * return injectTierGuards(spec, runtimeState);
 * ```
 */
export function injectTierGuards(spec: Spec, state: DDRuntimeState): Spec {
  if (!spec.elements || typeof spec.elements !== 'object') {
    return spec;
  }

  for (const element of Object.values(spec.elements)) {
    const el = element as UIElement;

    if (!el || typeof el.type !== 'string') {
      continue;
    }

    const gate = PREMIUM_COMPONENT_GATES[el.type];
    if (gate) {
      const props = (el.props as Record<string, unknown>) ?? {};
      // Overwrite visible[] unconditionally — LLM hallucination cannot bypass
      el.visible = gate.visibility(props);
    }
  }

  // Seed runtime state in the spec so visibility conditions evaluate correctly.
  // The renderer merges this with its own runtimeState (set from server-verified
  // useTier() — see S3 #786). Having it in spec.state also enables SSR.
  spec.state = {
    ...(spec.state ?? {}),
    tier: state.tier,
    userId: state.userId,
    analysisId: state.analysisId,
  };

  return spec;
}

// ============================================================================
// Exported guard builders (used in tests and downstream stories)
// ============================================================================

/**
 * Canonical VisibilityCondition for PatternSpotlight.
 * Tier must NOT be 'free' (drama_plus or dramax).
 */
export function patternSpotlightGuard(): VisibilityCondition {
  return PREMIUM_COMPONENT_GATES['PatternSpotlight'].visibility({});
}

/**
 * Canonical VisibilityCondition for TransformationInsight.
 * Tier must be 'dramax' AND impactScore >= 6.
 */
export function transformationInsightGuard(impactScore: number): VisibilityCondition {
  return PREMIUM_COMPONENT_GATES['TransformationInsight'].visibility({ impactScore });
}
